import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAlertStore } from '../../store/alertStore';
import { useLoadingStore } from '../../store/loadingStore';

interface Nota {
  id: string;
  fecha_creacion: string;
  mensaje: string;
  usuario_autor: string;
  activo: boolean;
}

interface NotasProps {
  entityType: 'licitaciones' | 'compras-agiles';
  id: number;
  onFetchMensajes: () => Promise<Nota[]>;
  onCrearMensaje: (mensaje: string) => Promise<Nota[]>;
  onEliminarMensaje: (mensajeId: string) => Promise<Nota[]>;
}

const Notas: React.FC<NotasProps> = ({ entityType, id, onFetchMensajes, onCrearMensaje, onEliminarMensaje }) => {
  const [notas, setNotas] = useState<Nota[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [nuevaNota, setNuevaNota] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const showAlert = useAlertStore((state) => state.showAlert);
  const setGlobalLoading = useLoadingStore((state) => state.setLoading);

  const fetchMensajes = useCallback(async () => {
    if (!id) return;
    
    try {
      setGlobalLoading(true);
      const mensajes = await onFetchMensajes();
      
      if (mensajes && mensajes.length > 0) {
        const mensajesActivos = mensajes.filter(mensaje => mensaje.activo);
        setNotas(mensajesActivos);
      } else {
        setNotas([]);
      }
    } catch (error) {
      console.error('Error al cargar mensajes:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al cargar las notas';
      showAlert(errorMessage, 'error');
    } finally {
      setGlobalLoading(false);
    }
  }, [id, onFetchMensajes, showAlert, setGlobalLoading]);

  useEffect(() => {
    fetchMensajes();
  }, [fetchMensajes]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNuevaNota('');
  };

  const handleAddNota = async () => {
    if (nuevaNota.trim() && !isCreating) {
      setIsCreating(true);
      try {
        const nuevosMensajes = await onCrearMensaje(nuevaNota.trim());
        if (nuevosMensajes && Array.isArray(nuevosMensajes)) {
          const mensajesActivos = nuevosMensajes.filter(mensaje => mensaje.activo);
          setNotas(mensajesActivos);
          setNuevaNota('');
          setOpenDialog(false);
        }
      } catch (error) {
        console.error('Error al crear nota:', error);
      } finally {
        setIsCreating(false);
      }
    }
  };

  const handleDeleteNota = async (id: string) => {
    try {
      const nuevosMensajes = await onEliminarMensaje(id);
      if (nuevosMensajes && Array.isArray(nuevosMensajes)) {
        const mensajesActivos = nuevosMensajes.filter(mensaje => mensaje.activo);
        setNotas(mensajesActivos);
      }
    } catch (error) {
      console.error('Error al eliminar nota:', error);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '14px', sm: '16px', md: '18px' } }}>
          Notas de {entityType === 'licitaciones' ? 'la licitación' : 'la compra ágil'}
        </Typography>
        <Tooltip title="Agregar Nota">
          <IconButton
            color="primary"
            onClick={() => setOpenDialog(true)}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {notas.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '14px', sm: '16px', md: '18px' } }} >
            No hay notas disponibles. Agrega la primera nota para comenzar.
          </Typography>
        </Paper>
      ) : (
        <List>
          {notas.map((nota, index) => (
            <React.Fragment key={nota.id}>
              <ListItem
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: '8px',
                  mb: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDeleteNota(nota.id)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                            color: '#1976d2',
                            fontWeight: 600
                          }}
                        >
                          {nota.usuario_autor}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            fontSize: { xs: '10px', sm: '11px', md: '12px' }
                          }}
                        >
                          {formatDate(nota.fecha_creacion)}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontSize: { xs: '14px', sm: '16px', md: '18px' },
                          color: 'text.primary'
                        }}
                      >
                        {nota.mensaje}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              {index < notas.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        keepMounted={false}
      >
        <DialogTitle>
          Agregar Nueva Nota
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
              Texto de la nota
            </Typography>
            <textarea
              autoFocus
              value={nuevaNota}
              onChange={(e) => setNuevaNota(e.target.value)}
              placeholder="Escribe aquí tu nota..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                fontSize: '14px',
                lineHeight: '1.5',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
                color: '#000000',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1976d2';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e0e0e0';
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleAddNota}
            variant="contained"
            disabled={!nuevaNota.trim() || isCreating}
            sx={{ textTransform: 'none' }}
          >
            {isCreating ? 'Creando...' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notas; 